import React, { useState, useEffect, useContext } from 'react';
import { 
    Box, Typography, Grid, Card, CardContent, Button, Divider, Chip, 
    ToggleButton, ToggleButtonGroup 
} from '@mui/material';
import QUDProgress from '../../../components/progress';

// Controladores y Componentes QUD
import { getAiPlans, subscribeToAiPlan } from '../../../controllers/company/ai/AiController';
import MultiOrderPaymentContainer from '../../../components/payments/MultiOrderPaymentContainer'; // Ajusta la ruta a donde lo tengas
import UserContext from '../../../navigation/context/userContext';

; 

const AiPlansPricing = ({ onPaymentSuccess }) => {
    const { userData } = useContext(UserContext); // Necesario para los props del pago
    const qudPlatformUuid = 'QUD';

    const [isLoading, setIsLoading] = useState(true);
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    
    // Almacena todos los planes (los 4)
    const [allPlans, setAllPlans] = useState([]);
    
    // Estado del Toggle (month o year)
    const [billingCycle, setBillingCycle] = useState('month'); 

    // Estados de Pago
    const [showPaymentView, setShowPaymentView] = useState(false);
    const [orderData, setOrderData] = useState(null);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        setIsLoading(true);
        try {
            const response = await getAiPlans();
            if (response.success) {
                setAllPlans(response.data);
            }
        } catch (error) {
            console.error("Error cargando planes de IA", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Filtramos para mostrar solo los planes que coinciden con el ciclo seleccionado en el Toggle
    const displayedPlans = allPlans.filter(plan => plan.interval === billingCycle);

    const handleCycleChange = (event, newCycle) => {
        if (newCycle !== null) {
            setBillingCycle(newCycle);
        }
    };

    const handleSubscribe = async (planId) => {
        setIsCreatingOrder(true);
        try {
            const response = await subscribeToAiPlan({ plan_id: planId });
            
            if (response.success) {
                // Laravel nos devuelve: order_uuid, order_id, total
                const { order_id, total } = response.data;
                
                setOrderData({ order_id, total });
                setShowPaymentView(true);
            } else {
                console.error("Error al crear la orden", response.message);
            }
        } catch (error) {
            console.error("Error crítico", error);
        } finally {
            setIsCreatingOrder(false);
        }
    };

    const handlePaymentComplete = (success) => {
        if (success) {
            // El componente padre (WhatsAppAiModule) debería recibir esto
            // para recargar el checkStatus() y pasar al Estado 1 (MetaOnboarding)
            if (onPaymentSuccess) onPaymentSuccess();
        }
    };

    if (isLoading) return <QUDProgress open={isLoading} />;

    // ---------------------------------------------------------
    // VISTA 2: PASARELA DE PAGOS
    // ---------------------------------------------------------
    if (showPaymentView && orderData) {
        return (
            <Box sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
                <Button sx={{ mb: 2 }} onClick={() => setShowPaymentView(false)}>
                    ← Volver a los planes
                </Button>
                
                {/* 
                  Tu componente armando el paymentData exactamente como lo exige:
                  { total_to_pay: X, orders: [{id: Y}] }
                */}
                <MultiOrderPaymentContainer 
                    paymentData={{
                        total_to_pay: orderData.total,
                        orders: [{ id: orderData.order_id }] // Aquí van los objetos con 'id'
                    }}
                    merchantUuid="QUD" 
                    customer={{
                        nombre: userData.entity.name,
                        apellido: userData.entity.last_name,
                        documento_tipo: userData.entity.document_type,
                        documento: userData.entity.document_number,
                        correo:userData.user.email,
                        prefijo:userData.user.prefijo,
                        telefono: userData.entity.phone,
                        profesion: userData.entity.profession
                    }}
                    onPaymentSuccess={handlePaymentComplete}
                />
            </Box>
        );
    }

    // ---------------------------------------------------------
    // VISTA 1: TARJETAS DE PRECIOS AGRUPADAS
    // ---------------------------------------------------------
    return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Potencia tu atención con un Agente IA
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                Automatiza las respuestas de tu comercio y aumenta tus ventas directamente desde WhatsApp.
            </Typography>

            {/* TOGGLE MENSUAL / ANUAL */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                <ToggleButtonGroup
                    color="primary"
                    value={billingCycle}
                    exclusive
                    onChange={handleCycleChange}
                    aria-label="Intervalo de facturación"
                >
                    <ToggleButton value="month" sx={{ px: 4, fontWeight: 'bold' }}>
                        Mensual
                    </ToggleButton>
                    <ToggleButton value="year" sx={{ px: 4, fontWeight: 'bold' }}>
                        Anual (Ahorra 16%)
                    </ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Grid container spacing={4} justifyContent="center">
                {displayedPlans.map((plan) => (
                    <Grid item xs={12} sm={6} md={5} key={plan.id}>
                        <Card elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 3, position: 'relative' }}>
                            <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                {plan.agent_mode === 'conversational' && (
                                    <Chip 
                                        label="Recomendado" 
                                        color="primary" 
                                        size="small" 
                                        sx={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', fontWeight: 'bold' }} 
                                    />
                                )}
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    {plan.name.replace(' - Mensual', '').replace(' - Anual', '')} {/* Limpiamos el nombre */}
                                </Typography>
                                <Typography variant="h3" fontWeight="bold" color="primary" sx={{ my: 2 }}>
                                    ${plan.price}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    {plan.billing_text}
                                </Typography>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'left' }}>
                                    {plan.agent_mode === 'linear' 
                                        ? '✔ Menús interactivos con botones\n✔ Respuestas predefinidas' 
                                        : '✔ Respuestas fluidas con IA avanzada\n✔ Análisis de intención de compra'}
                                </Typography>
                            </CardContent>
                            <Box sx={{ p: 3, pt: 0 }}>
                                <Button 
                                    variant={plan.agent_mode === 'conversational' ? "contained" : "outlined"} 
                                    fullWidth 
                                    size="large"
                                    onClick={() => handleSubscribe(plan.id)}
                                    disabled={isCreatingOrder}
                                >
                                    {isCreatingOrder ? 'Generando Pedido...' : 'Elegir Plan'}
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default AiPlansPricing;